import { useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, FileText, AlertTriangle, ArrowRight, LogIn, UserPlus } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { PasswordStrengthMeter } from "@/components/password-strength-meter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import BauwerkPlusLogo from "@assets/Bauwerkplus-Logo_1752694037272.webp";

export default function LoginPage() {
  const { login } = useAuth();
  const [, navigate] = useLocation();
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const success = await login({ email, password });
      if (success) {
        navigate("/dashboard");
      } else {
        setError("Ungültige Anmeldedaten. Bitte versuchen Sie es erneut.");
      }
    } catch {
      setError("Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    setIsLoading(true);
    try {
      await apiRequest("POST", "/api/auth/register", { email, password, name });
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      navigate("/dashboard");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "";
      if (message.includes("existiert bereits")) {
        setError("Diese E-Mail-Adresse ist bereits registriert.");
      } else {
        setError("Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: <Building2 className="w-6 h-6 text-bauwerk-teal" />,
      title: "Bauwerkserfassung",
      description: "Grunddaten, Fotos und technische Details strukturiert erfassen und verwalten"
    },
    {
      icon: <AlertTriangle className="w-6 h-6 text-bauwerk-teal" />,
      title: "Schadensdokumentation",
      description: "Schäden und Mängel direkt vor Ort erfassen, beschreiben und fotografisch festhalten"
    },
    {
      icon: <FileText className="w-6 h-6 text-bauwerk-teal" />,
      title: "Angebotserstellung",
      description: "Kalkulation und professionelles PDF-Angebot auf Knopfdruck erstellen"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-bauwerk-mint to-white">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <img 
            src={BauwerkPlusLogo} 
            alt="BauwerkPlus" 
            className="h-20 w-auto mx-auto mb-8"
          />
          <h1 className="text-4xl md:text-5xl font-bold text-bauwerk-teal mb-4">
            Digitale Bauwerkserfassung
          </h1>
          <p className="text-xl text-gray-700 mb-2">
            Professionelle Lösungen für nachhaltige Betonsanierung
          </p>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Dokumentieren Sie Bauprojekte digital, erfassen Sie Schäden strukturiert und erstellen Sie 
            professionelle Angebote – alles in einer benutzerfreundlichen Web-Anwendung.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {features.map((feature, index) => (
            <Card key={index} className="border-bauwerk-mint shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 p-3 bg-bauwerk-mint rounded-full w-fit">
                  {feature.icon}
                </div>
                <CardTitle className="text-bauwerk-teal">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-gray-600">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="border-bauwerk-teal shadow-xl mb-12">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-bauwerk-teal">Einfacher 4-Schritte Workflow</CardTitle>
            <CardDescription>Von der Erfassung bis zum fertigen Angebot</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                "Grunddaten erfassen",
                "Technische Details",
                "Schäden dokumentieren",
                "Angebot erstellen"
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-10 h-10 bg-bauwerk-teal text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                    {index + 1}
                  </div>
                  <p className="text-sm text-gray-700">{step}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <Card className="inline-block border-bauwerk-teal shadow-xl w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-bauwerk-teal">
                {isRegisterMode ? "Konto erstellen" : "Anmelden"}
              </CardTitle>
              <CardDescription>
                {isRegisterMode 
                  ? "Erstellen Sie Ihr BauwerkPlus Konto." 
                  : "Melden Sie sich an, um die Anwendung zu nutzen."}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8 pt-0">
              <form onSubmit={isRegisterMode ? handleRegister : handleLogin} className="space-y-4">
                {isRegisterMode && (
                  <div className="space-y-2 text-left">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Ihr vollständiger Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>
                )}
                <div className="space-y-2 text-left">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="ihre@email.at"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2 text-left">
                  <Label htmlFor="password">Passwort</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder={isRegisterMode ? "Mindestens 8 Zeichen" : "Passwort eingeben"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={isRegisterMode ? 8 : undefined}
                  />
                  {isRegisterMode && <PasswordStrengthMeter password={password} />}
                </div>
                {error && (
                  <p className="text-sm text-red-600">{error}</p>
                )}
                <Button
                  type="submit"
                  disabled={isLoading}
                  size="lg"
                  className="w-full bg-bauwerk-teal hover:bg-bauwerk-teal/90 text-white text-lg"
                >
                  {isLoading ? (
                    isRegisterMode ? "Registrieren..." : "Anmelden..."
                  ) : isRegisterMode ? (
                    <>
                      <UserPlus className="w-5 h-5 mr-2" />
                      Konto erstellen
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  ) : (
                    <>
                      <LogIn className="w-5 h-5 mr-2" />
                      Anmelden
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
                <div className="pt-2 text-center">
                  <button
                    type="button"
                    onClick={() => { setIsRegisterMode(!isRegisterMode); setError(""); }}
                    className="text-sm text-bauwerk-teal hover:underline"
                  >
                    {isRegisterMode 
                      ? "Bereits registriert? Hier anmelden" 
                      : "Noch kein Konto? Jetzt registrieren"}
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
